import React from 'react';
import { AppStoreUpgradeScreen } from '@/screens/force-upgrade/app-store-upgrade-screen';
import { AuthStackContainer } from '@/navigators/auth-stack';
import { AuthStatus } from '@/enums';
import { LogoLoadingIndicator } from '@/components/logo-loading-indicator';
import { NotAuthorizedScreen } from '@/screens/not-authorized-screen';
import { OfflineScreen } from '@/screens/offline-screen';
import { OTAUpgradeScreen } from '@/screens/force-upgrade/ota-upgrade-screen';
import { useMaybeUserContext } from '@/contexts/user-context';

export enum NavigationGuardStatus {
  APP_STORE_UPGRADE = 'APP_STORE_UPGRADE',
  AUTHENTICATED = 'AUTHENTICATED',
  NOT_AUTHENTICATED = 'NOT_AUTHENTICATED',
  NOT_AUTHORIZED = 'NOT_AUTHORIZED',
  OFFLINE_MODE = 'OFFLINE_MODE',
  OFFLINE_SCREEN = 'OFFLINE_SCREEN',
  OVER_THE_AIR_UPGRADE = 'OVER_THE_AIR_UPGRADE',
}

interface NavigationGuardProps {
  allowedStatuses?: NavigationGuardStatus[];
  appMinimumVersion: string | null;
  children: React.ReactNode;
  fallbackComponents?: Partial<
    Record<NavigationGuardStatus, React.ComponentType>
  >;
  navigationGuardStatus: NavigationGuardStatus;
}

/**
 * NavigationStackGuard protects navigation stacks based on authentication, network,
 * and app version status. It renders different screens based on the navigation guard status:
 *
 * - AUTHENTICATED / OFFLINE_MODE: Renders children (full access to app)
 * - NOT_AUTHENTICATED: Shows login/auth screens
 * - NOT_AUTHORIZED: Shows not authorized screen
 * - OFFLINE_SCREEN: Shows offline error screen
 * - APP_STORE_UPGRADE / OVER_THE_AIR_UPGRADE: Shows upgrade prompts
 *
 * @param allowedStatuses - Which statuses allow rendering children (default: AUTHENTICATED, OFFLINE_MODE)
 * @param appMinimumVersion - Minimum required app version for upgrades
 * @param children - Content to render when status is allowed
 * @param fallbackComponents - Custom components for specific statuses (overrides defaults)
 * @param navigationGuardStatus - Current navigation guard status from useNavigationGuardStatus hook
 *
 * @example
 * ```typescript
 * // Basic usage with useNavigationGuardStatus hook
 * const navigationGuardStatus = useNavigationGuardStatus();
 * const { appMinimumVersion } = useAppMinimumVersion();
 *
 * if (!navigationGuardStatus) {
 *   return <LogoLoadingIndicator />;
 * }
 *
 * return (
 *   <NavigationStackGuard
 *     appMinimumVersion={appMinimumVersion}
 *     navigationGuardStatus={navigationGuardStatus}
 *   >
 *     <YourStackNavigator />
 *   </NavigationStackGuard>
 * );
 * ```
 *
 * @example
 * ```typescript
 * // Custom fallback for specific status
 * <NavigationStackGuard
 *   appMinimumVersion={appMinimumVersion}
 *   navigationGuardStatus={navigationGuardStatus}
 *   fallbackComponents={{
 *     [NavigationGuardStatus.OFFLINE_SCREEN]: CustomOfflineScreen,
 *   }}
 * >
 *   <YourStackNavigator />
 * </NavigationStackGuard>
 * ```
 *
 * @example
 * ```typescript
 * // Restrict to only authenticated users (no offline mode)
 * <NavigationStackGuard
 *   allowedStatuses={[NavigationGuardStatus.AUTHENTICATED]}
 *   appMinimumVersion={appMinimumVersion}
 *   navigationGuardStatus={navigationGuardStatus}
 * >
 *   <YourStackNavigator />
 * </NavigationStackGuard>
 * ```
 */
/**
 * UserContextSyncGuard ensures UserContext is synchronized with navigationGuardStatus
 * before rendering children. This prevents race conditions where AuthContext updates
 * before UserContext finishes loading user data.
 *
 * This guard should be used by experiences that rely on UserContext (operator portal).
 * Experiences without UserContext (sales portal) should skip this guard.
 *
 * @example
 * ```typescript
 * <NavigationStackGuard navigationGuardStatus={navigationGuardStatus}>
 *   <UserContextSyncGuard navigationGuardStatus={navigationGuardStatus}>
 *     <AppStack />
 *   </UserContextSyncGuard>
 * </NavigationStackGuard>
 * ```
 */
export const UserContextSyncGuard: React.FC<{
  children: React.ReactNode;
  navigationGuardStatus: NavigationGuardStatus;
}> = ({ children, navigationGuardStatus }) => {
  const userContext = useMaybeUserContext();

  // For AUTHENTICATED or OFFLINE_MODE statuses, verify UserContext is synchronized
  if (
    navigationGuardStatus === NavigationGuardStatus.AUTHENTICATED ||
    navigationGuardStatus === NavigationGuardStatus.OFFLINE_MODE
  ) {
    // If UserContext is still loading, show loading indicator
    if (!userContext) {
      return <LogoLoadingIndicator />;
    }

    // If UserContext determined the user is not authorized (authenticated but no access),
    // show the NotAuthorized screen instead of children
    if (userContext.authStatus === AuthStatus.NotAuthorized) {
      return <NotAuthorizedScreen />;
    }

    // Verify UserContext authStatus matches the expected navigationGuardStatus
    const expectedAuthStatus =
      navigationGuardStatus === NavigationGuardStatus.AUTHENTICATED
        ? AuthStatus.Authenticated
        : AuthStatus.Offline;

    if (userContext.authStatus !== expectedAuthStatus) {
      // UserContext hasn't caught up yet - show loading to prevent race condition
      return <LogoLoadingIndicator />;
    }
  }

  // UserContext is synchronized or not required - safe to render children
  return <>{children}</>;
};

export const NavigationStackGuard: React.FC<NavigationGuardProps> = ({
  appMinimumVersion,
  children,
  allowedStatuses = [
    NavigationGuardStatus.AUTHENTICATED,
    NavigationGuardStatus.OFFLINE_MODE,
  ],
  fallbackComponents = {},
  navigationGuardStatus,
}) => {
  if (allowedStatuses.includes(navigationGuardStatus)) {
    return <>{children}</>;
  }

  const defaultFallbacks: Record<NavigationGuardStatus, React.ComponentType> = {
    [NavigationGuardStatus.APP_STORE_UPGRADE]: AppStoreUpgradeScreen,
    [NavigationGuardStatus.AUTHENTICATED]: () => <>{children}</>,
    [NavigationGuardStatus.NOT_AUTHENTICATED]: AuthStackContainer,
    [NavigationGuardStatus.NOT_AUTHORIZED]: NotAuthorizedScreen,
    [NavigationGuardStatus.OFFLINE_MODE]: () => <>{children}</>,
    [NavigationGuardStatus.OFFLINE_SCREEN]: OfflineScreen,
    [NavigationGuardStatus.OVER_THE_AIR_UPGRADE]: () => (
      <OTAUpgradeScreen appMinimumVersion={appMinimumVersion} />
    ),
  };

  const FallbackComponent =
    fallbackComponents[navigationGuardStatus] ||
    defaultFallbacks[navigationGuardStatus];

  return <FallbackComponent />;
};
